<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue";
import { useApiClient } from "@/api/client";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import { toastApiResult } from "@/toaster";
import { required } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import useDialog from "@/ui/composables/useDialog";
import router from "@/router/router";

// Components
import Modal from "@/ui/components/modal.vue";
import Switch from "@/ui/components/switch/switch.vue";
import DateSelectionWrapper from "@/modules/explorer/components/DateSelectionWrapper.vue";
import CurveMetaDataEdit from "@/modules/booking_experts/pages/Components/Modals/CurveMetaDataEdit.vue";
import CurveAlignmentMenu from "@/modules/booking_experts/pages/Components/Chart/Menus/CurveAlignmentMenu.vue";
import { createBezierCurve } from "@/modules/booking_experts/utils";
import DayOfWeek from "@/modules/explorer/components/PeriodFilterSelector/Components/DayOfWeek.vue";
import CurveApplyToPricelistModal from "@/modules/booking_experts/pages/Components/Modals/CurveApplyToPricelistModal.vue";
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;
import BexAdministrationDO = Modules.BookingExperts.Data.BexAdministrationDO;
import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;
import PriceModelCurveUsageDTO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveUsageDTO;
import { onBeforeRouteLeave } from "vue-router";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";

const dialog = useDialog();
const store = useCurveModelStore();
const api = useApiClient("bookingExperts");

// State
const startCurveSnapshot = ref<string>();
const curveId = Number(router.currentRoute.value.params.id);
const priceList = ref<number>();
const showBaseDataModal = ref(false);
const formIsValid = ref(true);
const selectedDayOfWeeks = ref<FilterValueDTO[]>([]);
const showPricelistApplicationModal = ref<boolean>(false);
const showCurveInUseDialog = ref<boolean>(false);
const curveUsages = ref<Record<number, PriceModelCurveUsageDTO[]>>({});
// Computed
const curveType = computed(() => store.currentCurve.type);
const isCustom = computed(() => curveType.value === "custom");
const hasPYData = computed(() => store.referenceData.y1.length > 0);
const hasCYData = computed(() => store.referenceData.y0.length > 0);

const pricelistIds = computed<number[]>(() => [
	...new Set(
		Object.values(curveUsages.value).flatMap((curves) => {
			return curves.map((c) => c.id);
		})
	),
]);
const pricelistCount = computed(() => pricelistIds.value.length);

const curveIsInUse = computed(() => {
	return store.currentCurve.usage_count > 0;
});

const infoMessages = {
	year_over_year: {
		primary: "Booking curves vary from day to day.",
		secondary:
			"Therefore, the resulting graph only illustrates the concept, rather than reflecting the actual model’s output.",
	},
	occupancy: {
		primary: "The occupancy curve model computes prices daily.",
		secondary:
			"The resulting graph is a representation rather than the actual result.",
	},
	// custom: {
	// 	primary: "Use CLICK or SHIFT + CLICK to add or remove control points",
	// 	secondary: "",
	// },
} as const;

// Actions
async function saveCurve() {
	if (curveIsInUse.value && Object.keys(curveUsages.value).length > 0) {
		showCurveInUseDialog.value = true;
		return;
	}

	const confirmed = await dialog.confirm({
		title: "Save curve model?",
		message: "Are you sure you want to save this curve?",
	});
	if (!confirmed) return;

	await doSaveNormally();
}

const doSaveAsNew = () => {
	showCurveInUseDialog.value = false;
	showPricelistApplicationModal.value = true;
};

const doSaveNormally = async () => {
	showCurveInUseDialog.value = false;
	showBaseDataModal.value = false;
	if (store.currentCurve.type === "year_over_year") {
		store.currentCurve.base_dataset = createBezierCurve(
			store.currentCurve.days,
			store.currentCurve.yoy_options.scaleTarget
		);
	}

	await toastApiResult(
		api.updatePriceModelCurve(store.currentCurve.id, store.currentCurve)
	);
	snapshotCurrentCurve();
};

async function updateBaseData() {
	store.referenceData.y0 = store.referenceData.y1 = [];
	const yearData = await api.getPriceModelCurvePreviousYearData(
		curveId,
		priceList.value,
		store.periodSelection,
		store.currentCurve.days,
		selectedDayOfWeeks.value.map((v) => v.id)
	);

	if (yearData.isOk()) {
		store.referenceData = yearData.value;
	}
	showBaseDataModal.value = false;
}

function toggleMoveDirection() {
	store.moveDirection =
		store.moveDirection === "vertical" ? "individual" : "vertical";
}

const administrations = ref<BexAdministrationDO[]>();
const selectedAdministration = ref<number>();
const fetchAdministrations = async () => {
	const response = await useApiClient("bookingExperts").getAdministrations();
	if (response.isOk()) {
		administrations.value = response.value;
		selectedAdministration.value = administrations.value[0]?.id;
	}
};

const hasLoadedCurveUsages = ref(false);

const fetchPricelistsWhereCurrentCurveIsApplied = async () => {
	const response = await api.getCurveUsages(store.currentCurve.id);

	hasLoadedCurveUsages.value = true;
	if (response.isOk()) {
		curveUsages.value = response.value;
	} else {
		curveUsages.value = {};
	}
};

onMounted(async () => {
	await fetchAdministrations();
	await fetchPricelistsWhereCurrentCurveIsApplied();
	snapshotCurrentCurve();
});

const curveHasChanges = computed(() => {
	if (!startCurveSnapshot.value) return false;
	return startCurveSnapshot.value !== JSON.stringify(store.currentCurve);
});

const snapshotCurrentCurve = () => {
	startCurveSnapshot.value = JSON.stringify(store.currentCurve);
};

async function onCurveApplied(newCurve: PriceModelCurveDO) {
	await router.push({
		name: "pricing.curve.edit",
		params: { id: newCurve.id },
	});
}

const priceLists = computed(() => {
	return store.sourcePriceLists.filter(
		(priceList) => priceList.administration_id == selectedAdministration.value
	);
});

const savingNormallyText = computed(
	() =>
		`Saving normally will update the curve on ${
			pricelistCount.value <= 1
				? "this pricelist"
				: `all ${pricelistCount.value} pricelists`
		}.`
);

onBeforeRouteLeave((to, from, next) => {
	unsavedChangesDialog("curve", curveHasChanges.value, next);
});
</script>

<template>
	<CurveApplyToPricelistModal
		v-model="showPricelistApplicationModal"
		:curve="store.currentCurve"
		:pricelist-ids="pricelistIds"
		:administrations="administrations"
		@applied="onCurveApplied"
	/>

	<Modal
		v-model:visible="showCurveInUseDialog"
		title="This curve is in use"
		icon="fa-duotone fa-triangle-exclamation"
		width="480px"
	>
		<p class="text-body-2 mb-3">
			This curve is currently used by the following
		</p>

		<v-expansion-panels>
			<v-expansion-panel
				v-for="(pricelists, administrationName) in curveUsages"
				:key="administrationName"
			>
				<v-expansion-panel-title
					class="px-4 bg-background ga-1"
					style="font-size: 1rem; font-weight: 400"
				>
					{{ administrationName }}
					<span class="opacity-60"> ({{ pricelists.length }}) </span>
				</v-expansion-panel-title>
				<v-expansion-panel-text
					class="overflow-y-auto"
					style="max-height: 250px"
				>
					<div v-for="pricelist in pricelists" :key="pricelist.id" class="my-1">
						{{ pricelist.name }}
					</div>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<p class="text-body-2 mt-3">
			{{ savingNormallyText }}
		</p>

		<template #actions>
			<v-btn variant="text" color="error" @click="showCurveInUseDialog = false">
				Cancel
			</v-btn>
			<v-btn variant="outlined" @click="doSaveNormally">Save normally</v-btn>
			<v-btn variant="flat" color="primary" @click="doSaveAsNew">
				Save as new curve
			</v-btn>
		</template>
	</Modal>

	<div class="d-flex justify-space-between">
		<!-- Left Section -->
		<div class="d-flex align-center ga-2">
			<CurveMetaDataEdit
				v-model:name="store.currentCurve.name"
				v-model:description="store.currentCurve.description"
				v-model:color="store.currentCurve.color"
				v-model:priceAdjustmentType="store.currentCurve.price_adjustment_type"
			/>

			<!-- Custom Type Controls -->
			<template v-if="isCustom">
				<v-divider vertical />
				<v-btn
					variant="flat"
					color="secondary"
					size="small"
					prepend-icon="fa-regular fa-arrows-up-down-left-right"
					@click="toggleMoveDirection"
				>
					Move: {{ store.moveDirection }}
				</v-btn>
			</template>

			<!-- Curve Alignment -->
			<CurveAlignmentMenu />

			<template v-if="curveType in infoMessages">
				<v-divider vertical />
				<v-icon icon="fa-regular fa-square-info" class="text-medium-emphasis" />
				<div
					class="d-flex flex-column ga-1 text-medium-emphasis text-sm-caption"
				>
					<p style="line-height: 1">
						{{ infoMessages[curveType as keyof typeof infoMessages].primary }}
					</p>
					<p style="line-height: 1">
						{{ infoMessages[curveType as keyof typeof infoMessages].secondary }}
					</p>
				</div>
			</template>
		</div>

		<!-- Right Section -->
		<!-- TODO: Enable this again later-->
		<div class="d-flex align-center ga-2">
			<template v-if="store.currentCurve.type !== 'year_over_year'">
				<Switch v-if="hasPYData" v-model="store.showPY" label="PY" />
				<Switch v-if="hasCYData" v-model="store.showCY" label="CY" />
				<v-divider v-if="hasPYData || hasCYData" vertical />
				<v-btn
					variant="flat"
					color="secondary"
					size="small"
					@click="showBaseDataModal = true"
				>
					<v-icon icon="fa-regular fa-chart-mixed mr-1" />
					Reference Data
				</v-btn>
				<v-divider vertical />
			</template>
			<v-badge dot color="red" :model-value="curveHasChanges">
				<v-btn
					size="small"
					variant="flat"
					color="secondary"
					:disabled="!formIsValid || !hasLoadedCurveUsages"
					append-icon="fa-solid fa-save"
					@click="saveCurve"
					data-testid="save-curve"
				>
					Save
				</v-btn>
			</v-badge>
		</div>
	</div>

	<!-- Base Data Configuration Modal -->
	<v-dialog
		title="Visualize reference data"
		icon="fa-regular fa-chart-mixed"
		width="470"
		v-model="showBaseDataModal"
	>
		<v-card class="pt-4">
			<v-select
				v-if="administrations.length > 1"
				class="px-3 pb-3"
				v-model="selectedAdministration"
				label="Administration"
				:items="administrations"
				item-title="name"
				item-value="id"
				hide-details
			/>
			<v-select
				class="px-3"
				v-model="priceList"
				label="Price list"
				:items="priceLists"
				:rules="[required(t)]"
				item-title="name"
				item-value="id"
				hide-details
			/>

			<date-selection-wrapper
				v-model="store.periodSelection"
				@close="updateBaseData"
				:show-presets="false"
			>
				<template v-slot:aboveSubmitButton>
					<DayOfWeek v-model="selectedDayOfWeeks" />
				</template>
			</date-selection-wrapper>

			<!--			<v-btn-->
			<!--				color="primary"-->
			<!--				variant="flat"-->
			<!--				:disabled="!priceList"-->
			<!--				@click="updateBaseData"-->
			<!--			>-->
			<!--				Save-->
			<!--			</v-btn>-->
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
:deep(.v-field__append-inner) {
	font-size: 0.7em;
}
</style>
